import React, { useState, useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import ReactECharts from 'echarts-for-react';
import api from '../api/axios';
import { useTranslation } from 'react-i18next';
import * as moment from "moment";
import BLUE_SHADES, { getBlueShade } from '../constants/chartColors';
import { InformationCircleIcon, XMarkIcon, UserIcon } from '@heroicons/react/24/outline';
import { formatMinutes } from '../utils';
import Table from '../components/Table';
function AssociatesTable({ data }) {
  const { t } = useTranslation();
  if (!data || data.length === 0) return null;

  const columns = [
    { header: t('associate'), accessor: 'user', sortable: true },
    { header: t('daysWorked'), accessor: 'daysWorked', sortable: true, align: 'right' },
    { header: t('totalHours'), accessor: 'totalHours', sortable: true, align: 'right' },
    { header: t('avgHoursDay'), accessor: 'averageHours', sortable: true, align: 'right', render: (row) => Number(row.averageHours).toFixed(2) },
    { header: t('expectedHours'), accessor: 'expectedWorkingHours', sortable: true, align: 'right' },
    { header: t('dueHours'), accessor: 'totalDueHours', sortable: true, align: 'right', className: 'text-blue-400' },
    { header: t('overtimeH'), accessor: 'totalOverTimeHours', sortable: true, align: 'right', className: 'text-blue-400' },
  ];

  return (
    <div className="bg-gray-800 rounded-2xl shadow w-full max-w-full">
      <h2 className="text-lg sm:text-xl p-4 sm:p-5 font-semibold border-b border-gray-700">
        {t('associatesBreakdown')}
      </h2>
      <Table
        data={data}
        columns={columns}
        defaultSortColumn={0}
        keyExtractor={(row) => row.userId}
        containerClassName=""
      />
    </div>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  color = 'text-white',
  percentage,
  progressColor = 'bg-gradient-to-r from-blue-400 to-blue-800',
  showPercentage = false,
  onClick = () => { },
  clickable = false,
  tooltip = null
}) {
  const hasProgress = typeof percentage === 'number' && percentage >= 0 && percentage <= 100;
  percentage = Math.min(100, percentage);
  value = value || 0;

  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (hasProgress) {
      setTimeout(() => {
        setWidth(percentage);
      }, 50); // small delay triggers transition
    }
  }, [percentage]);

  return (
    <div onClick={onClick} style={{ cursor: clickable ? 'pointer' : 'default' }} className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow hover:shadow-lg transition relative overflow-visible hover:z-50">
      {hasProgress && (
        <div
          className={`absolute inset-y-0 left-0 ${progressColor} opacity-20 transition-all rounded-s-2xl duration-1000 ease-out`}
          style={{ width: `${width}%` }}
        />
      )}

      <div className="relative z-10">
        <div className="flex items-center gap-2 text-white-400 text-sm font-medium">
          <span>{title}</span>

          {tooltip && (
            <div className="relative group">
              {/* Question mark icon */}
              <InformationCircleIcon className="w-5 h-5 text-gray-400 hover:text-blue-400 cursor-pointer transition-colors" />

              {/* Tooltip */}
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-max max-w-xs 
                            bg-black text-white text-xs rounded-md px-2 py-1 
                            opacity-0 group-hover:opacity-100 transition pointer-events-none"  style={{ zIndex: 9999 }}>
                {tooltip}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-baseline justify-between mt-2">
          <div className={`text-2xl sm:text-3xl font-bold ${color}`}>
            {value}
          </div>

          {hasProgress && showPercentage && (
            <div
              className="absolute text-2xl sm:text-3xl lg:text-7xl font-black tracking-tight"
              style={{
                color: "#94a3b8",
                right: 0,
                top: 0,
                opacity: "20%"
              }}
            >
              {Math.round(percentage)}%
            </div>
          )}
        </div>

        {subtitle && (
          <div className="text-gray-500 text-sm mt-1">{subtitle}</div>
        )}
      </div>
    </div>
  );
}

function DailySummaryTable({ data }) {
  const { t } = useTranslation();
  if (!data || data.length === 0) {
    return <div className="text-gray-500 py-4 text-center">{t('noDailyDataAvailable')}</div>;
  }

  const columns = [
    { header: t('date'), accessor: 'date', sortable: true, className: 'font-medium' },
    { header: t('present'), accessor: 'attendance', sortable: true, align: 'right' },
    { header: t('workedH'), accessor: 'hours', sortable: true, align: 'right' },
    { header: t('expectedH'), accessor: 'expected', sortable: true, align: 'right' },
    { header: t('dueH'), accessor: 'due', sortable: true, align: 'right', className: 'text-orange-400' },
    { header: t('overtimeH'), accessor: 'overtime', sortable: true, align: 'right', className: 'text-yellow-400' },
    { header: t('avgHPerson'), accessor: 'avgHours', sortable: true, align: 'right', render: (row) => row.avgHours || '—' },
    { header: t('attendancePct'), accessor: 'attendancePct', sortable: true, align: 'right', render: (row) => `${row.attendancePct}%` },
  ];

  return (
    <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow overflow-x-auto">
      <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('dailySummary')}</h2>
      <Table
        data={data}
        columns={columns}
        defaultSortColumn={0}
        keyExtractor={(row) => row.date}
        containerClassName=""
      />
    </div>
  );
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [associatesData, setAssociatesData] = useState([]);
  const [currentDayData, setCurrentDayData] = useState({});
  const [modalData, setModalData] = useState({});
  const [associateData, setAssociateData] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isEventsModalOpen, setIsEventsModalOpen] = useState(false);
  const wsRef = useRef(null);

  const { t } = useTranslation();

  const showEventModal = (type = 'all') => {
    if (type == 'all') setModalData(currentDayData)
    else if (type == 'active') setModalData({ events: currentDayData.events.filter(d => d.checkOutLocal == null && d.checkInLocal != null) })
    else if (type == 'left') setModalData({ events: currentDayData.events.filter(d => d.checkOutLocal != null) })
    setIsEventsModalOpen(true);
  }

  const [metrics, setMetrics] = useState({
    totalHours7d: 0,
    avgHoursPerDay: 0,
    attendanceRateAvg: '—',
    totalAttendanceDays: 0,
    totalOvertime7d: 0,
    totalDueHours7d: 0,
  });

  const showNotification = (text) => {
    const id = Date.now();

    const audio = new Audio("/popup.mp3");
    audio.play().catch((err) => console.log("Audio play error:", err));

    setNotifications((prev) => [...prev, { id, text }]);

    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 6000);
  };

  const fetchAssociateData = async () => {
    try {
      const companyId = localStorage.getItem("companyId");
      if (!companyId) throw new Error('Company ID not found');

      let start = moment().startOf("d").subtract(7, "days").utc().toISOString();
      let end = moment().endOf("D").utc().toISOString();

      const res = await api.get(`/metrics/${companyId}/associates?startDate=${start}&endDate=${end}&new=true`);

      const raw = res.data.data || [];

      const aggregated = raw.reduce((acc, entry) => {
        const key = entry.userId;
        if (!acc[key]) {
          acc[key] = {
            userId: key,
            user: entry.userTitle || entry.userId,
            totalHours: 0,
            averageHours: 0,
            avgLateMinutes: 0,
            avgEarlyMinutes: 0,
            totalOverTimeHours: 0,
            totalDueHours: 0,
            expectedWorkingHours: 0,
            daysWorked: 0,
            daysWithData: new Set(),
          };
        }

        acc[key].totalHours += entry.totalHours || 0;
        acc[key].avgLateMinutes += Number(entry.avgLateMinutes).toFixed(2) || 0;
        acc[key].avgEarlyMinutes += Number(entry.avgEarlyMinutes).toFixed(2) || 0;
        acc[key].averageHours += entry.averageHours || 0;
        acc[key].totalOverTimeHours += entry.totalOverTimeHours || 0;
        acc[key].totalDueHours += entry.totalDueHours || 0;
        acc[key].expectedWorkingHours += entry.expectedWorkingHours || 0;
        acc[key].daysWithData.add(entry.DateLocal);
        acc[key].daysWorked = acc[key].daysWithData.size;

        return acc;
      }, {});

      const aggregatedArray = Object.values(aggregated).map(item => ({
        ...item
      }));

      setAssociateData(aggregatedArray);

      if (aggregatedArray.length > 0) {
        const totalHours = aggregatedArray.reduce((s, d) => s + d.totalHours, 0);
        const averageHours = aggregatedArray.reduce((s, d) => s + d.averageHours, 0);
        const totalOvertime = aggregatedArray.reduce((s, d) => s + d.totalOverTimeHours, 0);
        const totalDue = aggregatedArray.reduce((s, d) => s + d.totalDueHours, 0);
        const avgHours = (averageHours / aggregatedArray.length).toFixed(1);

        setMetrics({
          totalHours,
          totalOvertime,
          totalDue,
          avgHoursPerAssociate: Math.round(avgHours),
          associatesCount: aggregatedArray.length,
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || t('errorLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const ws = new WebSocket(process.env.REACT_APP_WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: "subscribe",
        companyId: localStorage.getItem("companyId")
      }));
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      console.log(message);

      if (message.type === "new_event_log") {
        refreshLiveMetrics();

        if (message.data?.notificationMessage) {
          showNotification(message.data.notificationMessage);
        }
      }
    };

    ws.onerror = (err) => {
      console.error("WS error", err);
    };

    ws.onclose = () => {
      console.log("WS closed");
    };

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const refreshLiveMetrics = async () => {
    const todayMetrics = await api.get(
      `/metrics/${localStorage.getItem("companyId")}/live_metrics`
    );

    const currentDay = todayMetrics.data.data || {};
    setCurrentDayData(currentDay);
  }

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const companyId = localStorage.getItem("companyId");

        if (!companyId) throw new Error(t('companyIdNotFound'));

        const today = new Date();
        const endDate = today.toISOString().split('T')[0];
        const startDateObj = new Date(today);
        startDateObj.setDate(today.getDate() - 6);
        const startDate = startDateObj.toISOString().split('T')[0];

        let start = moment().startOf("d").subtract(7, "days").utc().toISOString();
        let end = moment().endOf("D").utc().toISOString();

        const dailyRes = await api.get(
          `/metrics/${companyId}/daily?startDate=${start}&endDate=${end}`
        );

        const todayMetrics = await api.get(
          `/metrics/${companyId}/live_metrics`
        );

        const assocRes = await api.get(
          `/metrics/${companyId}/associates?startDate=${start}&endDate=${end}`
        );

        const daily = dailyRes.data.data || [];
        const associates = assocRes.data.data || [];
        const currentDay = todayMetrics.data.data || {};

        setCurrentDayData(currentDay);
        setDailyData(daily);
        setAssociatesData(associates);

        if (daily.length > 0) {
          const totalHours = daily.reduce((sum, d) => sum + (d.totalHours || 0), 0);
          const totalOvertime = daily.reduce((sum, d) => sum + (d.totalOverTimeHours || 0), 0);
          const totalDue = daily.reduce((sum, d) => sum + (d.totalDueHours || 0), 0);
          const avgAttendance = daily.length
            ? (daily.reduce((sum, d) => sum + (d.attendancePercentage || 0), 0) / daily.length).toFixed(1)
            : 0;

          setMetrics({
            totalHours7d: totalHours,
            avgHoursPerDay: daily.length ? (totalHours / daily.length).toFixed(1) : 0,
            attendanceRateAvg: `${avgAttendance}%`,
            totalAttendanceDays: daily.reduce((sum, d) => sum + (d.attendanceCount || 0), 0),
            totalOvertime7d: totalOvertime,
            totalDueHours7d: totalDue,
          });
        }
      } catch (err) {
        console.log(err);

        setError(err.response?.data?.message || t('failedToLoadDashboardData'));
      } finally {
        setLoading(false);
      }
    };

    fetchAssociateData();
    fetchDashboardData();
  }, []);

  const chartData = dailyData
    .map(day => {
      const attendance = day.attendanceCount || 0;
      const totalHours = day.totalHours || 0;
      const expected = day.expectedWorkingHours || 0;
      const due = day.totalDueHours || 0;
      const avgHours = attendance > 0 ? Number((totalHours / attendance).toFixed(1)) : 0;
      const attendancePct = Number((day.attendancePercentage || 0).toFixed(1));

      return {
        date: day.DateLocal || '—',
        attendance,
        hours: totalHours,
        expected,
        due,
        overtime: day.totalOverTimeHours || 0,
        avgHours,
        attendancePct,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  const dates = chartData.map(d => d.date);

  const userTotals = associatesData.reduce((acc, record) => {
    const user = record.userTitle || record.userId;
    if (!acc[user]) {
      acc[user] = { overtime: 0, hours: 0, expected: 0, due: 0 };
    }
    acc[user].overtime += record.totalOverTimeHours || 0;
    acc[user].hours += record.totalHours || 0;
    acc[user].expected += record.expectedWorkingHours || 0;
    acc[user].due += record.totalDueHours || 0;
    return acc;
  }, {});

  const topOvertime = Object.entries(userTotals)
    .map(([name, stats]) => ({ name, overtime: stats.overtime }))
    .filter(u => u.overtime > 0)
    .sort((a, b) => b.overtime - a.overtime)
    .slice(0, 5);

  console.log(topOvertime, userTotals);


  const shortShifts = Object.entries(userTotals)
    .map(([name, stats]) => ({ name, hours: stats.hours, expected: stats.expected }))
    .filter(u => u.hours < u.expected * 0.75)
    .sort((a, b) => a.hours - b.hours)
    .slice(0, 5);

  const commonChartOptions = {
    tooltip: { trigger: 'axis' },
    grid: { left: '4%', right: '4%', bottom: '12%', top: '15%', containLabel: true },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { color: '#aaa', rotate: 45, interval: 0 },
      axisLine: { lineStyle: { color: '#444' } }
    },
  };

  if (loading) return <div className="p-10 text-center text-gray-300">{t('loadingDashboard')}</div>;
  if (error) return <div className="p-10 text-center text-red-400">{t('error')}: {error}</div>;

  const names = associateData.map(item => item.user);

  const VISIBLE_BAR_COUNT = 8;
  const BAR_HEIGHT = 42;
  const HEADER_HEIGHT = 50;
  const CHART_HEIGHT = HEADER_HEIGHT + VISIBLE_BAR_COUNT * BAR_HEIGHT;
  const MIN_CHART_WIDTH = 0;

  const getBaseDataSorted = (sortKey) => {
    let data = associateData;
    if (typeof sortKey === "string") {
      data = associateData.sort((a, b) => a[sortKey] - b[sortKey]);
    } else {
      data = associateData.sort((a, b) => (a[sortKey[0]] + a[sortKey[1]]) - (b[sortKey[0]] + b[sortKey[1]]));
    }

    associateData.reverse();

    const names = data.map(item => item.user);
    const hours = data.map(item => item.totalHours);
    const overtime = data.map(item => item.totalOverTimeHours);
    const due = data.map(item => item.totalDueHours);
    const avgDaily = data.map(item => Number(item.averageHours).toFixed(2));
    const avgLateMinutes = data.map(item => item.avgLateMinutes);
    const avgEarlyMinutes = data.map(item => item.avgEarlyMinutes);

    return {
      baseOptions: {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: {
          left: '1%',
          right: '14%',
          top: '2%',
          bottom: '2%',
          containLabel: true,
        },
        yAxis: {
          type: 'category',
          data: names,
          inverse: true,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#ddd',
            fontSize: 13,
            margin: 12,
            interval: 0,
          },
        },
        xAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#666' } },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#444', type: 'dashed' } },
          axisLabel: { color: '#aaa', formatter: `{value} ${t('hours')}` },
        },
      },
      names,
      hours,
      overtime,
      due,
      avgLateMinutes,
      avgEarlyMinutes,
      avgDaily
    }
  }

  const totalsBase = getBaseDataSorted("totalHours");
  const overTimeBase = getBaseDataSorted(["totalDueHours", "totalOverTimeHours"]);
  const avgBase = getBaseDataSorted("averageHours");
  const avgLatencyBase = getBaseDataSorted("avgLateMinutes");

  const totalHoursOption = {
    ...totalsBase.baseOptions,
    series: [{
      type: 'bar',
      data: totalsBase.hours,
      barWidth: 28,
      itemStyle: { color: BLUE_SHADES.primary, borderRadius: [0, 6, 6, 0] },
      label: {
        show: true,
        position: 'right',
        distance: 8,
        formatter: `{c} ${t('hours')}`,
        color: '#eee',
        fontSize: 12,
      },
    }],
  };

  const overtimeDueOption = {
    ...overTimeBase.baseOptions,
    legend: {
      data: [t('overtime'), t('dueMissing')],
      textStyle: { color: '#ddd' },
      top: 0,
      right: 10,
    },
    series: [
      {
        name: t('overtime'),
        type: 'bar',
        stack: 'total',
        data: overTimeBase.overtime,
        barWidth: 28,
        itemStyle: { color: getBlueShade(1), borderRadius: [0, 6, 6, 0] },
      },
      {
        name: t('dueMissing'),
        type: 'bar',
        stack: 'total',
        data: overTimeBase.due,
        barWidth: 28,
        itemStyle: { color: BLUE_SHADES.light2, borderRadius: [0, 6, 6, 0] },
      },
    ],
  };

  const avgDailyOption = {
    ...avgBase.baseOptions,
    series: [{
      type: 'bar',
      data: avgBase.avgDaily,
      barWidth: 28,
      itemStyle: { color: BLUE_SHADES.dark1, borderRadius: [0, 6, 6, 0] },
      label: {
        show: true,
        position: 'right',
        distance: 8,
        formatter: `{c} ${t('hoursPerDay')}`,
        color: '#eee',
        fontSize: 12,
      },
    }],
  };

  const latencyOption = {
    ...avgLatencyBase.baseOptions,
    legend: {
      data: [t('lateMinutesAvg'), t('earlyArrivalMinutesAvg')],
      textStyle: { color: '#ddd' },
      top: 0,
      right: 10,
    },
    series: [
      {
        name: t('lateMinutesAvg'),
        type: 'bar',
        stack: 'total',
        data: avgLatencyBase.avgLateMinutes,
        barWidth: 28,
        itemStyle: { color: getBlueShade(1), borderRadius: [0, 6, 6, 0] },
      },
      {
        name: t('earlyArrivalMinutesAvg'),
        type: 'bar',
        stack: 'total',
        data: avgLatencyBase.avgEarlyMinutes,
        barWidth: 28,
        itemStyle: { color: BLUE_SHADES.light2, borderRadius: [0, 6, 6, 0] },
      },
    ],
  };


  return (
    <div className="p-4 sm:p-6 text-white space-y-6 sm:space-y-8 min-h-screen bg-gray-900">
      <div className="fixed top-5 right-10 z-50 space-y-3">
        {notifications.map((n) => (
          <div
            key={n.id}
            className="flex items-start gap-3 bg-gray-800 border border-gray-700 text-white shadow-lg rounded-xl px-4 py-3 w-[400px] animate-[fadeIn_.3s_ease]"
          >
            <InformationCircleIcon className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />

            <div className="text-sm whitespace-pre-line flex-1">
              {n.text}
            </div>

            <button
              onClick={() =>
                setNotifications((prev) => prev.filter((x) => x.id !== n.id))
              }
              className="text-gray-400 hover:text-white"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>
      <h1 className="text-2xl sm:text-3xl font-bold text-blue-400">{t('dashboardTitle')} • {t('todayMetrics')}</h1>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-5">
        <MetricCard title={t('totalHours')} percentage={currentDayData.workHoursCoveragePercentage} showPercentage={true} value={formatMinutes(currentDayData.minutesWorked)} subtitle={''} color="text-blue-400" />
        <MetricCard title={t('attendanceRate')} percentage={currentDayData.attendancePercentage} showPercentage={true} value={currentDayData.attendancePercentage} subtitle={''} color="text-blue-400" />
        <MetricCard title={t('overtimeHours')} value={currentDayData.totalOverTimeHours} subtitle={''} color="text-blue-400" />
        <MetricCard title={t('officeVisits')} tooltip={t('officeVisitsTooltip')} clickable={true} onClick={() => showEventModal('all')} value={currentDayData.visitedOffice} percentage={currentDayData.attendancePercentage} showPercentage={true} subtitle={''} color="text-blue-400" />
        <MetricCard title={t('officeActive')} tooltip={t('officeActiveTooltip')} clickable={true} onClick={() => showEventModal('active')} value={currentDayData.visitedOffice - currentDayData.leftOffice} subtitle={''} color="text-blue-400" />
        <MetricCard title={t('alreadyLeft')} tooltip={t('officeLeftTooltip')} clickable={true} onClick={() => showEventModal('left')} percentage={currentDayData.leftPercentage} showPercentage={true} value={currentDayData.leftOffice} subtitle={''} color="text-blue-400" />
      </div>

      <h1 className="text-2xl sm:text-3xl font-bold text-blue-400">{t('dashboardTitle')} • {t('lastSevenDays')}</h1>

      <div className="space-y-8">
        <div className="grid lg:grid-cols-2 gap-5 sm:gap-8">
          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('attendanceAvgHours')}</h2>
            <ReactECharts
              option={{
                ...commonChartOptions,
                yAxis: [
                  { type: 'value', name: t('present'), nameTextStyle: { color: '#aaa' }, axisLine: { lineStyle: { color: '#3b82f6' } } },
                  { type: 'value', name: t('avgHPerson'), nameTextStyle: { color: '#aaa' }, position: 'right', axisLine: { lineStyle: { color: '#10b981' } } }
                ],
                series: [
                  {
                    name: t('present'),
                    type: 'line',
                    yAxisIndex: 0,
                    data: chartData.map(d => d.attendance),
                    itemStyle: { color: '#3b82f6' },
                    lineStyle: { width: 2 }
                  },
                  {
                    name: t('avgHPerson'),
                    type: 'line',
                    yAxisIndex: 1,
                    data: chartData.map(d => d.avgHours),
                    itemStyle: { color: '#10b981' },
                    lineStyle: { width: 2 }
                  }
                ]
              }}
              style={{ height: '288px' }}
            />
          </div>

          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('dailyOvertimeDueHours')}</h2>
            <ReactECharts
              option={{
                ...commonChartOptions,
                legend: { textStyle: { color: '#ddd' } },
                yAxis: { type: 'value', axisLabel: { color: '#aaa' }, axisLine: { lineStyle: { color: '#444' } } },
                series: [
                  {
                    name: t('overtimeHours'),
                    type: 'bar',
                    data: chartData.map(d => d.overtime),
                    itemStyle: { color: '#10b981' },
                    barMaxWidth: 40
                  },
                  {
                    name: t('dueMissingHours'),
                    type: 'bar',
                    data: chartData.map(d => d.due),
                    itemStyle: { color: '#3b82f6' },
                    barMaxWidth: 40
                  }
                ]
              }}
              style={{ height: '288px' }}
            />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 sm:gap-8">
        <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow">
          <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('topOvertime')}</h2>
          {topOvertime.length === 0 ? (
            <div className="text-gray-500 py-4">{t('noOvertimeRecorded')}</div>
          ) : (
            <ul className="space-y-3">
              {topOvertime.map((item, i) => (
                <li key={i} className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded">
                  <span>{item.name}</span>
                  <span className="text-blue-400 font-bold">{item.overtime} h</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow">
          <h2 className="text-xl
 mb-4 font-semibold">{t('shortUnderHours')}</h2>
          {shortShifts.length === 0 ? (
            <div className="text-gray-500 py-4">{t('noShortShiftsDetected')}</div>
          ) : (
            <ul className="space-y-3">
              {shortShifts.map((item, i) => (
                <li key={i} className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded">
                  <span>{item.name}</span>
                  <span className="text-red-400 font-bold">{item.hours} / {item.expected} h</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>


      <div className="space-y-8 pb-6">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow flex flex-col">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('totalHours')}</h2>
            <div className="flex-1 overflow-hidden">
              <div className="overflow-x-auto" style={{ maxHeight: `${CHART_HEIGHT}px` }}>
                <ReactECharts
                  option={totalHoursOption}
                  style={{
                    height: `${HEADER_HEIGHT + names.length * BAR_HEIGHT}px`,
                    width: "100%",
                  }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow flex flex-col">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('overtimeHours')} & {t('dueMissingHours')}</h2>
            <div className="flex-1 overflow-hidden">
              <div className="overflow-x-auto" style={{ maxHeight: `${CHART_HEIGHT}px` }}>
                <ReactECharts
                  option={overtimeDueOption}
                  style={{
                    height: `${HEADER_HEIGHT + names.length * BAR_HEIGHT}px`,
                    width: "100%",
                  }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadowflex flex-col">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('avgDailyHours')}</h2>
            <div className="flex-1 overflow-hidden">
              <div className="overflow-x-auto" style={{ maxHeight: `${CHART_HEIGHT}px` }}>
                <ReactECharts
                  option={avgDailyOption}
                  style={{
                    height: `${HEADER_HEIGHT + names.length * BAR_HEIGHT}px`,
                    width: "100%",
                  }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow flex flex-col">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('avgLatencyEarly')}</h2>
            <div className="flex-1 overflow-hidden">
              <div className="overflow-x-auto" style={{ maxHeight: `${CHART_HEIGHT}px` }}>
                <ReactECharts
                  option={latencyOption}
                  style={{
                    height: `${HEADER_HEIGHT + names.length * BAR_HEIGHT}px`,
                    width: "100%",
                  }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </div>
            </div>
          </div>
        </div>

        <AssociatesTable data={associateData} />
      </div>

      {isEventsModalOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-2 sm:p-4 bg-gray-900/80 backdrop-blur-sm transition-opacity" onClick={(e) => { if (e.target === e.currentTarget) setIsEventsModalOpen(false); }}>
          <div className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-7xl 2xl:max-w-[90vw] max-h-[96vh] sm:max-h-[90vh] flex flex-col overflow-hidden border border-gray-700 animate-[fadeIn_0.2s_ease-out]">
            <div className="p-3 sm:p-5 border-b border-gray-700 flex justify-between items-center bg-gray-800/80 sticky top-0 z-10">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <span className="text-blue-400">{t('officeVisits')}</span>
              </h2>
              <button
                onClick={() => setIsEventsModalOpen(false)}
                className="text-gray-400 hover:text-white hover:bg-gray-700 p-1.5 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-3 sm:p-5 overflow-auto flex-1 custom-scrollbar">
              {!modalData?.events || modalData.events.length === 0 ? (
                <div className="text-center text-gray-500 py-10">
                  {t('noEventsFound')}
                </div>
              ) : (
                <>
                  {/* Desktop View */}
                  <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-700">
                    <Table
                      data={modalData.events}
                      defaultSortColumn={1}
                      keyExtractor={(event, i) => event._id || i}
                      containerClassName=""
                      headerClassName="bg-gray-700/50"
                      rowClassName="hover:bg-gray-700/30 transition-colors"
                      columns={[
                        {
                          header: t('user'),
                          accessor: 'userName',
                          sortAccessor: (row) => {
                            const fName = row.user?.firstName || '';
                            const lName = row.user?.lastName || '';
                            return ((fName || lName) ? `${fName} ${lName}` : (row.userName || row.userId)).trim();
                          },
                          render: (event) => {
                            const fName = event.user?.firstName || '';
                            const lName = event.user?.lastName || '';
                            const fullName = (fName || lName) ? `${fName} ${lName}`.trim() : (event.userName || event.userId);
                            const roleName = event.role?.name || 'N/A';
                            const displayName = `${fullName} - ${roleName}`;
                            return (
                              <div className="flex items-center gap-2.5 font-medium whitespace-nowrap">
                                <UserIcon className={`w-4 h-4 shrink-0 ${event.checkOutLocal ? 'text-red-500' : 'text-green-500'}`} />
                                <span>{displayName}</span>
                              </div>
                            );
                          }
                        },
                        { header: t('checkIn'), accessor: 'checkInLocal', align: 'right', className: 'whitespace-nowrap' },
                        { header: t('checkOut'), accessor: 'checkOutLocal', align: 'right', className: 'whitespace-nowrap' },
                        {
                          header: t('workedHours'),
                          accessor: 'hoursWorked',
                          align: 'right',
                          render: (event) => <span className="font-medium">{event.hoursWorked ? event.hoursWorked.toFixed(2) : '—'}</span>
                        },
                        {
                          header: t('lateMinutes'),
                          accessor: 'shift.lateMinutes',
                          align: 'right',
                          sortAccessor: (row) => row.shift?.lateMinutes || 0,
                          render: (event) => event.shift?.lateMinutes || 0
                        },
                        {
                          header: t('arrival'),
                          accessor: 'arrival',
                          sortAccessor: (row) => Math.max(row.shift?.lateMinutes || 0, row.shift?.earlyMinutes || 0),
                          align: 'right',
                          className: 'whitespace-nowrap',
                          render: (event) => {
                            const lateMinutes = event.shift?.lateMinutes || 0;
                            const earlyMinutes = event.shift?.earlyMinutes || 0;
                            if (lateMinutes > 0 || earlyMinutes > 0) {
                              const minutes = lateMinutes > 0 ? lateMinutes : earlyMinutes;
                              const label = lateMinutes > 0 ? t('late') : t('early');
                              const dotColor = lateMinutes >= 10 ? 'bg-red-500' : 'bg-green-500';
                              return (
                                <div className="flex items-center justify-end gap-2 text-right">
                                  <span className="font-medium">{event.shift?.name || 'N/A'} — {minutes}{t('minutes')} {label}</span>
                                  <div className={`w-2 h-2 rounded-full ${dotColor}`}></div>
                                </div>
                              );
                            }
                            return (
                              <div className="flex items-center justify-end gap-2 text-right">
                                <span className="font-medium">{event.shift?.name || 'N/A'} — {t('onTime')}</span>
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              </div>
                            );
                          }
                        },
                        { header: 'Shift', accessor: 'shift.name', align: 'right', className: 'text-gray-400 whitespace-nowrap' }
                      ]}
                    />
                  </div>

                  {/* Mobile View */}
                  <div className="sm:hidden space-y-3">
                    {modalData.events.map((event, i) => {
                      const checkInLocal = event.checkInLocal || '—';
                      const checkOutLocal = event.checkOutLocal || '—';
                      const lateMinutes = event.shift?.lateMinutes || 0;
                      const earlyMinutes = event.shift?.earlyMinutes || 0;
                      const hoursWorked = event.hoursWorked ? event.hoursWorked.toFixed(2) : '—';

                      const fName = event.user?.firstName || '';
                      const lName = event.user?.lastName || '';
                      const fullName = (fName || lName) ? `${fName} ${lName}`.trim() : (event.userName || event.userId);
                      const roleName = event.role?.name || 'N/A';
                      const displayName = `${fullName} - ${roleName}`;

                      let arrivalDisplay = (
                        <span className="text-green-400 font-medium">{t('onTime')}</span>
                      );

                      if (lateMinutes > 0 || earlyMinutes > 0) {
                        const minutes = lateMinutes > 0 ? lateMinutes : earlyMinutes;
                        const label = lateMinutes > 0 ? t('late') : t('early');
                        const color = lateMinutes > 0 ? 'text-red-400' : 'text-green-400';
                        arrivalDisplay = (
                          <span className={`font-medium text-[11px] leading-tight ${color}`}>{minutes}{t('minutes')} {label}</span>
                        );
                      }

                      return (
                        <div key={event._id || i} className="bg-gray-900 rounded-xl p-4 border border-gray-700">
                          <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-800">
                            <div className="flex items-start gap-3">
                              <UserIcon className={`w-5 h-5 shrink-0 mt-0.5 ${event.checkOutLocal ? 'text-red-500' : 'text-green-500'}`} />
                              <div>
                                <div className="font-bold text-sm text-white leading-tight">{displayName}</div>
                                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                  <span className="text-xs text-gray-400">{event.shift?.name || t('noShift')}</span>
                                  <span className="text-gray-700 text-[10px] select-none">•</span>
                                  {arrivalDisplay}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                            <div>
                              <div className="text-gray-500 text-xs">{t('checkIn')}</div>
                              <div className="font-medium">{checkInLocal}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs">{t('checkOut')}</div>
                              <div className="font-medium">{checkOutLocal}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs">{t('workedHours')}</div>
                              <div className="font-medium text-blue-400">{formatMinutes(event.minutesWorked)}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs">{t('lateMinutes')}</div>
                              <div className="font-medium">{lateMinutes}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}